/**
 * TODO reconcile this implementation with app/NameAndTags
 *
 * this is currently only used by GenericForm
 * app/NameAndTags is used by ExperimentForm as well as PageComponentsList
 */
import React, { Component } from 'react';
import propTypes from 'prop-types';
import { Button, FormFeedback, Input, InputGroup, InputGroupAddon, Label } from 'reactstrap';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';

import shortid from 'shortid';

import Chip from 'app/Chip/components/Chip';
import {
  COMPONENT_NAME_LOCATION,
  DYNAMIC_COMPONENT_ROUTE,
  NAME_TAGS_LOCATION,
  USER_TAGS_LOCATION
} from 'lib/const';
import { utcToPtDateString } from 'lib/date';
import { createTags } from 'lib/string';
import { IconButton } from 'ui/Components';
import { favorite, unfavorite } from 'ui/Icons';

import HelpIcon from '../../CanvasWidgets/components/HelpIcon';

/**
 * render a form component for component names and tagging
 */
export default class NameAndTags extends Component {
  static propTypes = {
    component: propTypes.object.isRequired,
    isCompassComponent: propTypes.bool,
    isDynamic: propTypes.bool,
    isFavorite: propTypes.bool,
    isFetchingComponent: propTypes.bool,
    isLive: propTypes.bool,
    isReadonly: propTypes.bool,
    name: propTypes.string,
    minTagInputLength: propTypes.number,
    nameValidationMessage: propTypes.string,
    onFavoriteClick: propTypes.func,
    onNameChange: propTypes.func.isRequired,
    modify: propTypes.func.isRequired,
    route: propTypes.string,
    routeValidationMessage: propTypes.string,
    showFavoriteIcon: propTypes.bool,
    startDate: propTypes.string,
    audienceTags: propTypes.array.isRequired,
    tags: propTypes.shape({
      NameTags: propTypes.arrayOf(propTypes.string),
      UserTags: propTypes.arrayOf(propTypes.string)
    }).isRequired
  };

  static defaultProps = {
    isCompassComponent: false,
    isDynamic: false,
    isFavorite: false,
    isFetchingComponent: false,
    isLive: false,
    isReadonly: false,
    minTagInputLength: 2,
    name: 'Component Name',
    nameValidationMessage: undefined,
    onFavoriteClick: undefined,
    route: '',
    routeValidationMessage: undefined,
    showFavoriteIcon: false,
    startDate: ''
  };

  /**
   * constructor for this component
   * @param {object} props - props to this component
   */
  constructor(props) {
    super(props);

    this.state = {
      name: props.name || '',
      route: props.route || '',
      audienceTagText: '',
      tagText: ''
    };

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleRouteChange = this.handleRouteChange.bind(this);
    this.handleAudienceInputSelection = this.handleAudienceInputSelection.bind(this);
    this.handleTextInputChange = this.handleTextInputChange.bind(this);
    this.handleUserTagAdd = this.handleUserTagAdd.bind(this);
    this.clearInput = this.clearInput.bind(this);
    this.setInputRef = this.setInputRef.bind(this);
    this.setValueInState = this.setValueInState.bind(this);
  }

  /**
   * change name
   * @param {object} event - event containing the new component name
   */
  handleNameChange(event) {
    const { modify, name, onNameChange } = this.props;
    const newName = event.target.value.trim();
    const NameTags = createTags(newName);
    if (name !== newName) {
      onNameChange(newName);
      modify(NameTags, NAME_TAGS_LOCATION);
      modify(newName, COMPONENT_NAME_LOCATION);
    }
  }

  /**
   * handles delete user tags event
   * @param {string} tag - tag text being deleted
   */
  handleDeleteTag(tag) {
    const { modify, tags } = this.props;

    const newUserTags = tags.UserTags.filter(uTagText => uTagText !== tag);
    modify(newUserTags, USER_TAGS_LOCATION);
  }

  /**
   * update internal state for text inputs
   * @param {object} event - text input event
   */
  handleTextInputChange(event) {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  /**
   * update internal state for audience tag input selection
   * @param {Array} selection - input selection
   */
  handleAudienceInputSelection(selection) {
    const [value] = selection;
    const audienceCode = value.split('|')[0].toUpperCase();
    this.setState({ audienceTagText: audienceCode });
  }

  handleRouteChange(event) {
    const { modify, route } = this.props;

    const newRoute = event.target.value.trim();

    if (newRoute !== route) {
      modify(newRoute, DYNAMIC_COMPONENT_ROUTE);
    }
  }

  /**
   * add user tag and clear tag input
   */
  handleUserTagAdd() {
    const {
      minTagInputLength,
      modify,
      tags: { UserTags: userTags = [] }
    } = this.props;

    const trimmedTag = this.state.tagText.trim() || this.state.audienceTagText?.trim();

    if (
      trimmedTag &&
      trimmedTag.length >= minTagInputLength &&
      !userTags.includes(trimmedTag)
    ) {
      modify([...userTags, trimmedTag], USER_TAGS_LOCATION);
    }

    this.setState({ tagText: '', audienceTagText: '' });
    this.clearInput();
  }

  /**
   * set a ref to the search input dom element
   * @param {Element} typeahead - a ref to the input element
   */
  setInputRef(typeahead) {
    if (!this.input) {
      this.input = typeahead.getInstance();
    }
  }

  /**
   * set correct value (name or route) in state
   * @param {object} params - params
   * @param {string} params.value - value to set in state
   * @param {string} params.type - type of state value
   */
  setValueInState({ value, type }) {
    this.setState({ [type]: value });
  }

  /**
   * clear search input
   */
  clearInput() {
    if (this.input) {
      this.input.clear();
    }
  }

  makeRouteText() {
    return (
      <div>
        <p>Corresponding routes for the following components</p>
        <ul>
          <li>SEO Content Block: Brand ID_seo</li>
          <li>Global Messaging: Global_Messaging</li>
          <li>About the Brand: Brand ID_atb</li>
          <li>Global Header Copy Group: Nordstrom Global Header</li>
          <li>Global Footer Banner Group: Global Footer Site Wide Banners</li>
          <li>Store Amenities: Store Images</li>
        </ul>
      </div>
    );
  }

  renderRouteInfo() {
    const { isReadonly, routeValidationMessage } = this.props;
    const { route } = this.state;

    return (
      <div className="mt-2">
        <div data-test-id="nametags-route">
          <Label className="font-weight-bold mr-2">Route </Label>
          <HelpIcon id="route-help-icon" message={this.makeRouteText} />
        </div>
        <Input
          className="rounded-left"
          name="route"
          type="text"
          value={route}
          invalid={routeValidationMessage}
          onChange={this.handleTextInputChange}
          onBlur={this.handleRouteChange}
          placeholder="Add Route..."
          data-test-id="nametags-route-input"
          disabled={isReadonly}
        />
        <FormFeedback>{routeValidationMessage}</FormFeedback>
      </div>
    );
  }

  /**
   * render information about scheduled/live dynamic component
   * @returns {React.Component} information about dynamic component
   */
  renderLiveInfo() {
    const { isLive, startDate } = this.props;

    const isScheduled = !!startDate && new Date().toISOString() < startDate;

    // no info to show
    if (!isLive && !isScheduled) {
      return null;
    }

    return (
      <div data-test-id="nametags-live-date">
        <Label className="font-weight-bold mr-2 mt-3">
          {isLive ? 'Live now as of:' : 'Live date scheduled for:'}
        </Label>
        {utcToPtDateString(startDate, 'L LT z')}
      </div>
    );
  }

  render() {
    const {
      component: { ComponentName: name, ComponentRoute: route },
      isCompassComponent,
      isDynamic,
      isFavorite,
      isFetchingComponent,
      isReadonly,
      onFavoriteClick,
      showFavoriteIcon,
      audienceTags,
      tags,
      nameValidationMessage
    } = this.props;

    if (isFetchingComponent) {
      if (name && name !== this.state.name) {
        this.setValueInState({ value: name, type: 'name' });
      } else if (route && route !== this.state.route) {
        this.setValueInState({ value: route, type: 'route' });
      }
    }

    // sort audienceTags AlphaNumerically (AUD1, AUD2, AUDX1, etc.)
    audienceTags.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    return (
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-top">
          <Label className="font-weight-bold">Name</Label>
          {showFavoriteIcon && (
            <IconButton
              id={this.id}
              icon={isFavorite ? favorite : unfavorite}
              isReadonly={isReadonly}
              noMargin
              onClick={onFavoriteClick}
              title={isFavorite ? 'Mark Unfavorite' : 'Mark Favorite'}
              rem={2.4}
            />
          )}
        </div>
        <Input
          className="rounded"
          invalid={nameValidationMessage}
          name="name"
          type="text"
          value={this.state.name}
          onChange={this.handleTextInputChange}
          onBlur={this.handleNameChange}
          data-test-id="nametags-name-input"
          disabled={isReadonly}
        />
        <FormFeedback>{nameValidationMessage}</FormFeedback>
        <div className="font-weight-bold mt-3">User Tags</div>
        <InputGroup>
          <Input
            className="rounded-left"
            name="tagText"
            type="text"
            value={this.state.tagText}
            onChange={this.handleTextInputChange}
            placeholder="Add Tag..."
            data-test-id="nametags-tag-input"
            disabled={isReadonly}
            onKeyPress={event => {
              if (event.key === 'Enter') {
                this.handleUserTagAdd();
              }
            }}
          />
          <InputGroupAddon className="rounded-right" addonType="append">
            <Button
              className="rounded-right px-4"
              color="primary"
              disabled={isReadonly}
              onClick={this.handleUserTagAdd}
              data-test-id="nametags-tag-add-button"
            >
              +
            </Button>
          </InputGroupAddon>
        </InputGroup>
        <div className="font-weight-bold mt-3">Audience Tags</div>
        <InputGroup>
          <Typeahead
            disabled={isReadonly}
            className="rounded-left"
            id="typeahead-audience-tags"
            options={audienceTags}
            paginate={false}
            placeholder="Search by Keyword..."
            onChange={this.handleAudienceInputSelection}
            ref={this.setInputRef}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                this.handleUserTagAdd();
              }
            }}
            renderMenu={(results, menuProps) => (
              <Menu {...menuProps} maxHeight={475}>
                {results.map((result, index) => (
                  <MenuItem
                    option={result}
                    position={index}
                    title={result}
                    style={{ width: '250%' }}
                  >
                    {result}
                  </MenuItem>
                ))}
              </Menu>
            )}
          />
          <InputGroupAddon className="rounded-right" addonType="append">
            <Button
              className="rounded-right px-4"
              color="primary"
              disabled={isReadonly}
              onClick={this.handleUserTagAdd}
              data-test-id="story-tag-add-button"
            >
              +
            </Button>
          </InputGroupAddon>
        </InputGroup>
        <div className="d-flex flex-wrap mt-3">
          {tags.NameTags.map(text => (
            <Chip
              key={shortid.generate()}
              className="mr-2 mb-2"
              leftColor="info"
              rightColor="info"
              rightText={text}
              hideClose
              isReadonly={isReadonly}
            />
          ))}
          {tags.UserTags.map(text => (
            <Chip
              key={shortid.generate()}
              className="mr-2 mb-2"
              leftColor="primary"
              rightColor="primary"
              rightText={text}
              onClose={() => this.handleDeleteTag(text)}
              isReadonly={isReadonly}
            />
          ))}
        </div>
        {isDynamic && !isCompassComponent && this.renderRouteInfo()}
        {isDynamic && this.renderLiveInfo()}
      </div>
    );
  }
}
